import { formatCustomerName } from './formatCustomerName';
import { formatNuf } from './formatNuf';

const normalize = value => String(value ?? '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();
const code = value => {
  const text = normalize(value).replace(/ /g, '');
  return /^\d+$/.test(text) ? text.replace(/^0+(?=\d)/, '') : text;
};
const nameWords = value => normalize(value).split(' ').filter(Boolean).sort();
const nearCode = (a, b) => a.length === b.length && [...a].filter((char, i) => char !== b[i]).length === 1;
const labels = { customerName: 'Nome OCR', customerCode: 'Cod. OCR', invoice: 'Fatt. OCR', amount: 'Importo OCR' };
const conflictKeys = { customerName: 'nomeCliente', customerCode: 'codiceCliente', invoice: 'numeroFattura', amount: 'importo' };
const currency = value => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(value));

export function compareOcrField(field, { customer, invoices = [], amount } = {}, conflicts = []) {
  let state = 'uncompared', expected = '', message = 'Seleziona il riferimento per confrontare la lettura.';
  if (field.kind === 'customerName' && formatCustomerName(customer, '')) {
    expected = formatCustomerName(customer, '');
    const actualWords = nameWords(field.value), expectedWords = nameWords(expected);
    state = actualWords.join(' ') === expectedWords.join(' ') ? 'exact'
      : actualWords.some(word => expectedWords.includes(word)) ? 'partial' : 'mismatch';
  } else if (field.kind === 'customerCode' && customer?.cod_cli != null && String(customer.cod_cli).trim()) {
    expected = String(customer.cod_cli);
    state = code(field.value) === code(expected) ? 'exact' : nearCode(code(field.value), code(expected)) ? 'partial' : 'mismatch';
  } else if (field.kind === 'invoice' && invoices.length) {
    expected = invoices.map(inv => formatNuf(inv.sezionale, inv.nuf)).join(', ');
    const ref = field.reference;
    if (ref?.nuf) {
      const exact = invoices.filter(inv => Number(inv.nuf) === Number(ref.nuf)
        && (!ref.sezionale || code(inv.sezionale) === code(ref.sezionale))
        && (!ref.year || String(inv.f_emission || '').slice(0, 4) === String(ref.year)));
      state = exact.length === 1 ? (!ref.sezionale && code(exact[0].sezionale) ? 'partial' : 'exact')
        : exact.length > 1 ? 'partial'
          : invoices.some(inv => (!ref.sezionale || code(inv.sezionale) === code(ref.sezionale))
            && (!ref.year || String(inv.f_emission || '').slice(0, 4) === String(ref.year))
            && Number(inv.nuf) !== Number(ref.nuf) && nearCode(String(inv.nuf), String(ref.nuf))) ? 'partial' : 'mismatch';
    }
  } else if (field.kind === 'amount' && amount != null && Number.isFinite(Number(field.value))) {
    expected = currency(amount);
    state = Math.round(Number(field.value) * 100) === Math.round(Number(amount) * 100) ? 'exact' : 'mismatch';
  }
  if (state !== 'uncompared') message = `${state === 'exact' ? 'Corrispondenza esatta' : state === 'partial' ? 'Corrispondenza parziale o ambigua' : 'Dato discordante'}. Riferimento: ${expected}.`;
  const uncertain = conflicts.includes(conflictKeys[field.kind]) || field.confidence == null
    || !Number.isFinite(Number(field.confidence)) || Number(field.confidence) < 70;
  if (uncertain) message += conflicts.includes(conflictKeys[field.kind]) ? ' Le letture OCR sono discordanti.' : ' Lettura OCR da verificare.';
  return { state, color: state === 'mismatch' ? 'error' : state === 'exact' && !uncertain ? 'success' : 'warning', message,
    label: `${labels[field.kind]}: ${field.kind === 'amount' ? currency(field.value) : field.value}` };
}

export function storedOcrOverlay(slip, imageHash) {
  const analysis = slip?.analysis;
  if (!imageHash || analysis?.image_hash !== imageHash) return null;
  const reading = analysis.readings?.find(item => item.selected && item.layout?.version === 1);
  return reading ? { image_hash: imageHash, layout: reading.layout, conflicts: analysis.conflicts || [] } : null;
}

export function validOcrFields(layout) {
  return (layout?.version === 1 && Array.isArray(layout.fields) ? layout.fields : []).filter(field => labels[field.kind] && field.value != null
    && field.box && ['x','y','width','height'].every(key => Number.isFinite(field.box[key]))
    && field.box.x >= 0 && field.box.y >= 0 && field.box.width > 0 && field.box.height > 0
    && field.box.x + field.box.width <= 1.001 && field.box.y + field.box.height <= 1.001).slice(0, 64);
}

const overlaps = (a, b) => a.x < b.x + b.width + 3 && a.x + a.width + 3 > b.x
  && a.y < b.y + b.height + 3 && a.y + a.height + 3 > b.y;

// Keep badges near their fields without covering other badges or recognized field text.
export function placeOcrBadges(fields, width, height) {
  const placed = [];
  const protectedFields = fields.map(field => ({ x: field.box.x * width, y: field.box.y * height,
    width: field.box.width * width, height: field.box.height * height }));
  for (const field of [...fields].sort((a, b) => a.box.y - b.box.y || a.box.x - b.box.x)) {
    const badgeWidth = Math.min(Math.max(80, field.label.length * 6.5 + 30), 260, Math.max(40, width - 8));
    const anchorX = Math.max(4, Math.min(field.box.x * width, width - badgeWidth - 4));
    const lefts = [...new Set([anchorX, ...placed.flatMap(p => [p.x + p.width + 4, p.x - badgeWidth - 4])])]
      .filter(x => x >= 4 && x + badgeWidth <= width - 4).sort((a, b) => Math.abs(a - anchorX) - Math.abs(b - anchorX));
    if (!lefts.length) lefts.push(anchorX);
    const obstacles = [...protectedFields, ...placed.map(p => ({ x: p.x, y: p.y, width: p.width, height: 22 }))];
    let y = (field.box.y + field.box.height) * height + 3;
    let position;
    for (let attempt = 0; attempt <= obstacles.length + 1; attempt++) {
      const nextRows = [];
      for (const x of lefts) {
        const candidate = { x, y, width: badgeWidth, height: 22 };
        const collisions = obstacles.filter(obstacle => overlaps(candidate, obstacle));
        if (!collisions.length) { position = candidate; break; }
        nextRows.push(...collisions.map(obstacle => obstacle.y + obstacle.height + 4));
      }
      if (position) break;
      y = Math.max(y + 1, Math.min(...nextRows));
    }
    placed.push({ ...field, x: position?.x ?? anchorX, y: position?.y ?? y, width: badgeWidth });
  }
  return placed;
}
